import { useEffect, useRef, useState } from "react";
import { API_BASE, getToken } from "../api";

const MAX_SEC = 300; // 5 minutes

interface Props {
  onUploaded: (info: { audio_r2_key: string; audio_duration_sec: number }) => void;
  onCancel: () => void;
}

type Phase = "idle" | "recording" | "preview" | "uploading" | "denied" | "error";

export default function VoiceRecorder({ onUploaded, onCancel }: Props) {
  const [phase, setPhase] = useState<Phase>("idle");
  const [seconds, setSeconds] = useState(0);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const mediaRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const blobRef = useRef<Blob | null>(null);
  const startedAtRef = useRef<number>(0);
  const tickRef = useRef<number | null>(null);

  function fmt(s: number) {
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m}:${String(r).padStart(2, "0")}`;
  }

  async function start() {
    setErr(null);
    if (!navigator.mediaDevices?.getUserMedia) {
      setErr("Microphone non disponible sur cet appareil.");
      setPhase("error");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // MediaRecorder avec format compatible Whisper (webm/opus est universel ; Safari 14.5+ aussi)
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : MediaRecorder.isTypeSupported("audio/mp4")
          ? "audio/mp4"
          : "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      mediaRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mime || "audio/webm" });
        blobRef.current = blob;
        setPreviewUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach((t) => t.stop());
        setPhase("preview");
      };
      rec.start();
      startedAtRef.current = Date.now();
      setSeconds(0);
      setPhase("recording");
      tickRef.current = window.setInterval(() => {
        const s = Math.floor((Date.now() - startedAtRef.current) / 1000);
        setSeconds(s);
        if (s >= MAX_SEC) stop();
      }, 250);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Erreur micro";
      setErr(msg);
      setPhase("denied");
    }
  }

  function stop() {
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    mediaRef.current?.stop();
  }

  function discard() {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    blobRef.current = null;
    setPreviewUrl(null);
    setSeconds(0);
    setPhase("idle");
  }

  async function send() {
    if (!blobRef.current) return;
    setPhase("uploading");
    try {
      const buf = await blobRef.current.arrayBuffer();
      const res = await fetch(`${API_BASE}/voice-uploads`, {
        method: "POST",
        headers: {
          "Content-Type": blobRef.current.type || "audio/webm",
          "Authorization": `Bearer ${getToken()}`,
          "x-audio-duration": String(seconds),
        },
        body: buf,
      });
      if (!res.ok) throw new Error("Échec téléversement");
      const data = await res.json() as { r2_key: string; duration_sec: number };
      onUploaded({ audio_r2_key: data.r2_key, audio_duration_sec: data.duration_sec });
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Erreur");
      setPhase("preview");
    }
  }

  useEffect(() => () => {
    if (tickRef.current) clearInterval(tickRef.current);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    mediaRef.current?.state === "recording" && mediaRef.current.stop();
  }, [previewUrl]);

  return (
    <div className="card">
      <div className="section-h" style={{ margin: 0 }}>Message vocal</div>

      {phase === "idle" && (
        <>
          <p className="muted" style={{ marginTop: 8 }}>
            Cliquer pour démarrer l'enregistrement (5 min max). L'app demandera l'accès au micro.
          </p>
          <div className="row" style={{ gap: 8, marginTop: 10 }}>
            <button className="btn btn-primary" onClick={start}>🎤 Démarrer</button>
            <button className="btn btn-ghost" onClick={onCancel}>Annuler</button>
          </div>
        </>
      )}

      {phase === "recording" && (
        <>
          <div className="row" style={{ gap: 12, marginTop: 14, alignItems: "center" }}>
            <span style={{
              display: "inline-block", width: 12, height: 12, borderRadius: 999,
              background: "var(--terracotta)", animation: "pulse 1s ease-in-out infinite",
            }} />
            <span style={{ fontFamily: "var(--font-mono)", fontSize: "1.4rem", color: "var(--navy)" }}>
              {fmt(seconds)} <span className="tiny">/ {fmt(MAX_SEC)}</span>
            </span>
          </div>
          <div className="row" style={{ gap: 8, marginTop: 14 }}>
            <button className="btn btn-secondary" onClick={stop}>■ Stop</button>
            <button className="btn btn-ghost" onClick={() => { mediaRef.current?.stop(); discard(); onCancel(); }}>Annuler</button>
          </div>
          <style>{`@keyframes pulse { 0%,100% { opacity: 1 } 50% { opacity: 0.3 } }`}</style>
        </>
      )}

      {phase === "preview" && previewUrl && (
        <>
          <div className="muted" style={{ marginTop: 8 }}>
            Durée : {fmt(seconds)} — Réécoute avant envoi :
          </div>
          <audio src={previewUrl} controls style={{ width: "100%", marginTop: 8 }} />
          {err && <div className="error" style={{ marginTop: 8 }}>{err}</div>}
          <div className="row" style={{ gap: 8, marginTop: 12, flexWrap: "wrap" }}>
            <button className="btn btn-primary" onClick={send}>Envoyer</button>
            <button className="btn btn-ghost" onClick={discard}>Refaire</button>
            <button className="btn btn-ghost" onClick={() => { discard(); onCancel(); }}>Annuler</button>
          </div>
        </>
      )}

      {phase === "uploading" && (
        <p className="muted" style={{ marginTop: 12 }}>Envoi en cours…</p>
      )}

      {(phase === "denied" || phase === "error") && (
        <>
          <div className="error" style={{ marginTop: 8 }}>
            {err || "Accès au microphone refusé."}
          </div>
          <button className="btn btn-ghost" style={{ marginTop: 8 }} onClick={onCancel}>Fermer</button>
        </>
      )}
    </div>
  );
}
